/**
 * SignatureField - Canvas-based signature capture
 *
 * Task: T-P5-D4-F2-3
 * Feature: P5-D4-F2 Shared Form Contract Upgrade
 *
 * Pointer Events canvas that captures signature as PNG data URI.
 * DPR-aware sizing, clear button, 200KB blocking enforcement.
 * On pointerup: exports canvas, checks size. If oversize, restores
 * last valid state and shows warning. Uses FieldWrapper for chrome.
 */

import { useRef, useEffect, useCallback, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { SignatureFieldProps } from './types';
import { FieldWrapper } from './FieldWrapper';
import styles from './SignatureField.module.css';

/** 200KB in bytes */
const MAX_SIZE_BYTES = 200 * 1024;

/**
 * Estimate decoded byte size from a base64 data URI.
 * base64 encodes 3 bytes into 4 chars; header is excluded from size calc.
 */
function estimateDataUriBytes(dataUri: string): number {
  const base64Part = dataUri.split(',')[1];
  if (!base64Part) return 0;
  return Math.ceil(base64Part.length * 0.75);
}

export function SignatureField({
  field,
  value,
  onChange,
  onBlur,
  error,
  style,
  nonInteractive,
}: SignatureFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const lastValidDataUrl = useRef<string>(value || '');
  const [sizeWarning, setSizeWarning] = useState('');

  // Keep lastValidDataUrl in sync with external value changes
  useEffect(() => {
    lastValidDataUrl.current = value || '';
  }, [value]);

  // Set up DPR-aware canvas sizing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    // Restore existing signature if present
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = value;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getCtx = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.getContext('2d');
  }, []);

  const getCanvasCoords = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    },
    []
  );

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      const ctx = getCtx();
      if (!ctx) return;

      isDrawing.current = true;

      // A draw gesture (touch-drag with touch-action:none + pointer capture) suppresses
      // the synthetic click that would otherwise focus the canvas, so focus it explicitly
      // — this keeps the accent "selected" ring lit while signing, not just on a bare tap.
      e.currentTarget.focus({ preventScroll: true });

      const { x, y } = getCanvasCoords(e);

      // Ink follows the theme: the booking form sets --sig-ink to a light token so
      // the signature reads white on its dark inset; admin/legacy forms leave it
      // unset → black. (Inherited onto the canvas from .canvasContainer.)
      const canvas = canvasRef.current;
      const ink =
        (canvas && getComputedStyle(canvas).getPropertyValue('--sig-ink').trim()) || '#000000';

      ctx.lineCap = 'round';
      ctx.lineWidth = 2;
      ctx.strokeStyle = ink;
      ctx.beginPath();
      ctx.moveTo(x, y);

      // Capture pointer for reliable tracking outside canvas
      (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    },
    [getCtx, getCanvasCoords]
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (!isDrawing.current) return;
      const ctx = getCtx();
      if (!ctx) return;

      const { x, y } = getCanvasCoords(e);
      ctx.lineTo(x, y);
      ctx.stroke();
    },
    [getCtx, getCanvasCoords]
  );

  const handlePointerUp = useCallback(() => {
    if (!isDrawing.current) return;
    isDrawing.current = false;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL('image/png');
    const sizeBytes = estimateDataUriBytes(dataUrl);

    if (sizeBytes > MAX_SIZE_BYTES) {
      // Oversize: restore last valid state, show warning
      setSizeWarning('Signature is too large (over 200KB). The last stroke has been undone.');

      // Restore canvas to last valid state
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const rect = canvas.getBoundingClientRect();
        ctx.clearRect(0, 0, rect.width, rect.height);

        if (lastValidDataUrl.current) {
          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, 0, 0, rect.width, rect.height);
          };
          img.src = lastValidDataUrl.current;
        }
      }
    } else {
      // Within limit: commit
      setSizeWarning('');
      lastValidDataUrl.current = dataUrl;
      onChange(dataUrl);
    }
  }, [onChange]);

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
    }

    lastValidDataUrl.current = '';
    setSizeWarning('');
    onChange('');
  }, [onChange]);

  return (
    <FieldWrapper
      fieldId={field.fieldId}
      label={field.label}
      required={field.required}
      helpText={field.helpText}
      error={error}
      style={style}
    >
      <div className={`${styles.canvasContainer} ${error ? styles.hasError : ''}`}>
        <canvas
          ref={canvasRef}
          className={styles.canvas}
          // Focusable so the field shows the same accent "selected" ring as the other
          // booking inputs (a canvas is not focusable by default → :focus-within never fires).
          tabIndex={nonInteractive ? undefined : 0}
          style={{ touchAction: 'none', ...(nonInteractive ? { pointerEvents: 'none' } : undefined) }}
          onPointerDown={nonInteractive ? undefined : handlePointerDown}
          onPointerMove={nonInteractive ? undefined : handlePointerMove}
          onPointerUp={nonInteractive ? undefined : handlePointerUp}
          onPointerLeave={nonInteractive ? undefined : handlePointerUp}
          onBlur={onBlur}
          aria-label={field.label}
        />
        {!value && (
          <div className={styles.placeholder}>Sign here</div>
        )}
        {!nonInteractive && value && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
            aria-label="Clear signature"
          >
            Clear
          </button>
        )}
      </div>
      {sizeWarning && (
        <span className={styles.sizeWarning}>{sizeWarning}</span>
      )}
    </FieldWrapper>
  );
}
